<div class="pg">

  <header class="pg__head">
    <h1 class="pg__title">{{ 'PRACTICE.TITLE' | translate }}</h1>
    <p class="pg__sub">{{ 'PRACTICE.SUBTITLE' | translate }}</p>
  </header>

  <!-- ═══ 1. Matière ═══ -->
  <section class="pg__card">
    <div class="pg__card-head">
      <h2><span class="pg__step">1</span>{{ 'PRACTICE.STEP_SUBJECT' | translate }}</h2>
    </div>
    <div class="pg__card-body">
      @if (subjectsErrored()) {
        <app-error-state
          titleKey="PRACTICE.ERROR_TITLE"
          messageKey="PRACTICE.ERROR_DESC"
          (retry)="loadSubjects()"></app-error-state>
      } @else if (loadingSubjects()) {
        <div class="pg__skeleton"></div>
      } @else {
        <div class="pg__subjects">
          @for (s of subjects(); track s.id) {
            <button type="button" class="pg__subject"
                    [attr.aria-pressed]="s.id === subjectId()"
                    (click)="selectSubject(s.id)">
              <span class="pg__subject-name">{{ s.name }}</span>
            </button>
          }
        </div>
      }
    </div>
  </section>

  <!-- ═══ 2. Chapitres ═══ -->
  @if (subjectId()) {
    <section class="pg__card">
      <div class="pg__card-head">
        <h2><span class="pg__step">2</span>{{ 'PRACTICE.STEP_MODULES' | translate }}</h2>
        <p>{{ 'PRACTICE.MODULES_HELP' | translate }}</p>
      </div>
      <div class="pg__card-body">
        @if (loadingPool()) {
          <div class="pg__skeleton"></div>
        } @else {
          <div class="pg__modules">
            @for (theme of pool()?.themes ?? []; track theme.themeId) {
              @let free = theme.availableCount;
              <button type="button" class="pg__module"
                      [attr.aria-pressed]="picked().has(theme.themeId)"
                      [class.pg__module--thin]="free < 12"
                      (click)="toggleTheme(theme.themeId)">
                <span class="pg__module-check">
                  {{ picked().has(theme.themeId) ? '☑' : '☐' }}
                </span>
                <span>
                  <span class="pg__module-name">{{ theme.themeTitle }}</span>
                  <span class="pg__module-sub">
                    {{ 'PRACTICE.AVAILABLE' | translate }}: {{ free }}
                    @if (theme.recentlySeenCount > 0) {
                      · {{ 'PRACTICE.RECENTLY_SEEN' | translate: { count: theme.recentlySeenCount } }}
                    }
                  </span>
                </span>
                <span class="pg__mastery">
                  <span class="pg__mastery-bar">
                    <i [style.inline-size.%]="theme.masteryPercent ?? 0"
                       [style.background]="masteryColor(theme.masteryPercent)"></i>
                  </span>
                  <span class="pg__mastery-val">
                    {{ theme.masteryPercent !== null ? theme.masteryPercent + '%' : '—' }}
                  </span>
                </span>
              </button>
            }
          </div>
        }
      </div>
    </section>

    <!-- ═══ 3. Réglages ═══ -->
    <section class="pg__card">
      <div class="pg__card-head">
        <h2><span class="pg__step">3</span>{{ 'PRACTICE.STEP_HOW' | translate }}</h2>
      </div>
      <div class="pg__card-body">

        <div class="pg__modes">
          <button type="button" class="pg__mode"
                  [attr.aria-pressed]="mode() === 'learn'"
                  (click)="setMode('learn')">
            <b>📖 {{ 'PRACTICE.MODE_LEARN' | translate }}</b>
            <span>{{ 'PRACTICE.MODE_LEARN_DESC' | translate }}</span>
          </button>
          <button type="button" class="pg__mode"
                  [attr.aria-pressed]="mode() === 'revise'"
                  (click)="setMode('revise')">
            <b>🔁 {{ 'PRACTICE.MODE_REVISE' | translate }}</b>
            <span>{{ 'PRACTICE.MODE_REVISE_DESC' | translate }}</span>
          </button>
        </div>

        <div class="pg__opts">
          <div class="pg__opt">
            <label for="pg-count">{{ 'PRACTICE.COUNT' | translate }}</label>
            <input id="pg-count" type="number" min="5" max="40"
                   [value]="count()" (input)="setCount($any($event.target).value)">
          </div>
          <div class="pg__opt">
            <label for="pg-diff">{{ 'PRACTICE.DIFFICULTY' | translate }}</label>
            <select id="pg-diff" [value]="difficulty()"
                    (change)="setDifficulty($any($event.target).value)">
              <option value="auto">{{ 'PRACTICE.DIFFICULTY_AUTO' | translate }}</option>
              <option value="easy">{{ 'PRACTICE.DIFFICULTY_EASY' | translate }}</option>
              <option value="hard">{{ 'PRACTICE.DIFFICULTY_HARD' | translate }}</option>
            </select>
            <small>{{ 'PRACTICE.DIFFICULTY_HELP' | translate }}</small>
          </div>
        </div>

        <!-- Indicateur de diversité : la vérité avant de générer -->
        <div class="pg__diversity" [attr.data-level]="diversity()">
          @switch (diversity()) {
            @case ('none') {
              <span class="pg__diversity-ico">⚠️</span>
              <span><b>{{ 'PRACTICE.PICK_MODULE_FIRST' | translate }}</b></span>
            }
            @case ('low') {
              <span class="pg__diversity-ico">⚠️</span>
              <span>
                <b>{{ 'PRACTICE.DIVERSITY_LOW' | translate }}</b>
                <p>{{ 'PRACTICE.REUSED_WARNING' | translate }}</p>
              </span>
            }
            @case ('warn') {
              <span class="pg__diversity-ico">◐</span>
              <span><b>{{ 'PRACTICE.DIVERSITY_WARN' | translate: { count: distinctTests() } }}</b></span>
            }
            @case ('ok') {
              <span class="pg__diversity-ico">✓</span>
              <span><b>{{ 'PRACTICE.DIVERSITY_OK' | translate: { count: distinctTests() } }}</b></span>
            }
          }
        </div>

        <!-- Le bouton n'ouvre pas un aperçu : il démarre la passation. -->
        <button type="button" class="pg__cta"
                [disabled]="!canGenerate()"
                (click)="generate()">
          ▶ {{ (generating() ? 'PRACTICE.GENERATING' : 'PRACTICE.GENERATE') | translate }}
        </button>
        <p class="pg__cta-note">{{ 'PRACTICE.GENERATE_NOTE' | translate }}</p>
      </div>
    </section>
  }

</div>
